import { Checkbox, FormField } from '@expressable/syntax-ds';
import { FC } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DEFAULT_TIME_OPTIONS, TimeRange } from 'domains/signupDirect/misc/constants';

export type TimeOptionValue = {
  earlyMorning: boolean;
  morning: boolean;
  afternoon: boolean;
  evening: boolean;
};

type Props = {
  name: string;
  onTimeOptionsChange: (value: TimeOptionValue) => void;
};

export const TimeOptionsCheckboxes: FC<Props> = ({ name, onTimeOptionsChange }) => {
  const { t } = useTranslation();

  const {
    field: { value: timeOptionValues, onChange },
  } = useController<{ [key: string]: TimeOptionValue }>({
    name,
    defaultValue: DEFAULT_TIME_OPTIONS,
  });

  const handleCheckboxChange = (option: TimeRange, checked: boolean) => {
    const newValue = { ...timeOptionValues, [option]: checked };

    onChange(newValue);

    onTimeOptionsChange(newValue);
  };

  return (
    <FormField label={t('directSignup.schedule.single.timeOptions.label')} name={name}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-e12">
        <Checkbox
          variant="plain"
          checked={timeOptionValues.earlyMorning}
          onChange={e => handleCheckboxChange('earlyMorning', e.target.checked)}
          id="early-morning"
          data-testid="early-morning-checkbox"
        >
          <span className="hidden md:inline">
            {t('directSignup.schedule.single.timeOptions.options.desktop.earlyMorning')}
          </span>
          <span className="inline md:hidden">
            {t('directSignup.schedule.single.timeOptions.options.mobile.earlyMorning')}
          </span>
        </Checkbox>

        <Checkbox
          variant="plain"
          checked={timeOptionValues.morning}
          onChange={e => handleCheckboxChange('morning', e.target.checked)}
          id="morning"
          data-testid="morning-checkbox"
        >
          <span className="hidden md:inline">
            {t('directSignup.schedule.single.timeOptions.options.desktop.morning')}
          </span>
          <span className="inline md:hidden">
            {t('directSignup.schedule.single.timeOptions.options.mobile.morning')}
          </span>
        </Checkbox>

        <Checkbox
          variant="plain"
          checked={timeOptionValues.afternoon}
          onChange={e => handleCheckboxChange('afternoon', e.target.checked)}
          id="afternoon"
          data-testid="afternoon-checkbox"
        >
          <span className="hidden md:inline">
            {t('directSignup.schedule.single.timeOptions.options.desktop.afternoon')}
          </span>
          <span className="inline md:hidden">
            {t('directSignup.schedule.single.timeOptions.options.mobile.afternoon')}
          </span>
        </Checkbox>

        <Checkbox
          variant="plain"
          checked={timeOptionValues.evening}
          onChange={e => handleCheckboxChange('evening', e.target.checked)}
          id="evening"
          data-testid="evening-checkbox"
        >
          <span className="hidden md:inline">
            {t('directSignup.schedule.single.timeOptions.options.desktop.evening')}
          </span>
          <span className="inline md:hidden">
            {t('directSignup.schedule.single.timeOptions.options.mobile.evening')}
          </span>
        </Checkbox>
      </div>
    </FormField>
  );
};
